import { useContext, useState, useEffect } from "react";
import logo from "../../components/Assests/logo 1.png";
import user from "../../components/Assests/user.png";
import backgroundImage from "../../components/Assests/Global-Dashboard.png";
import { Navbar, Container, Nav, Dropdown, Image } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "./GlobalDashboard.css";
import { RoleContext } from "../../RoleContext";
import { getLocations } from "./GlobalDashboardApi";
import { getUserPhoto } from "../../../src/services/signInApi"
import { msalInstance } from "../../../src/authConfig";

const UserDashboardPath = {
  ["Lab Admin"]: "/admin-dashboard",
  ["IT Admin"]: "/admin-dashboard",
  ["Customer"]: "/customer-dashboard",
  ["Analyst"]: "/analyst-dashboard",
  ["Field Staff"]: "/field-staff-dashboard",
};

const getRoleRoute = (userRole) => {
  if (!userRole) return null;

  const normalizedUserRole = userRole.toLowerCase();

  for (const [key, value] of Object.entries(UserDashboardPath)) {
    if (key.toLowerCase() === normalizedUserRole) {
      return value;
    }
  }
  return null;
};

const GlobalDashboard = ({ expanded }) => {
  const navigate = useNavigate();
  const { role } = useContext(RoleContext);
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("Select location");
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [userPhoto, setUserPhoto] = useState(null);

  useEffect(() => {
    const loadPhoto = async () => {
      try {
        if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
          msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
        }

        const photoUrl = await getUserPhoto();
        if (photoUrl) {
          setUserPhoto(photoUrl);
        } else {
          setUserPhoto(user);
        }
      } catch (error) {
        console.error("Failed to load user photo:", error);
        setUserPhoto(user);
      }
    };

    loadPhoto();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);

        const userDataString = localStorage.getItem('userData');

        if (!userDataString) {
          console.error('No user data found - redirecting to login');
          navigate("/");
          return;
        }

        try {
          const userData = JSON.parse(userDataString);

          const fetchedName = userData.name ||
            userData.fullName ||
            userData.username ||
            userData.displayName;

          if (!fetchedName) {
            console.error('No valid user name found in userData');
            setUserName("User");
          } else {
            setUserName(fetchedName);
          }

          const savedLocationId = localStorage.getItem("selectedLocationId");
          if (!savedLocationId && userData.locationName && userData.locationID) {
            setSelectedLocation(userData.locationName);
            localStorage.setItem("selectedLocationId", userData.locationID.toString());
            localStorage.setItem("selectedLocationName", userData.locationName.toString());
          }

        } catch (parseError) {
          console.error('Failed to parse user data:', parseError);
          navigate("/");
          return;
        }

      } catch (error) {
        console.error('Failed to fetch user data:', error);
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLocationsLoading(true);

        if (!localStorage.getItem('userData')) {
          navigate("/");
          return;
        }

        const response = await getLocations();

        if (response.success) {
          const activeLocations = response.results?.filter(location => location.isActive) || [];
          setLocations(activeLocations);

          const savedLocationId = localStorage.getItem("selectedLocationId");
          if (savedLocationId && activeLocations.length > 0) {
            const savedLocation = activeLocations.find(location =>
              location.locationId?.toString() === savedLocationId
            );
            if (savedLocation) {
              setSelectedLocation(savedLocation.name);
            } else {
              localStorage.removeItem("selectedLocationId");
              setSelectedLocation("Select location");
            }
          }
        } else {
          console.error('Failed to fetch locations');
          setLocations([]);
        }
      } catch (error) {
        console.error('Error fetching locations:', error);
        setLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };

    fetchLocations();
  }, [navigate]);

  const handleSignOut = () => {
    localStorage.removeItem("userData");
    localStorage.removeItem("currentRole");
    localStorage.removeItem("token");
    localStorage.removeItem("selectedLocationId");
    navigate("/");
  };

  const handleLocationChange = (locationId, locationName) => {
    if (!locationId || !locationName) {
      console.error('Invalid location data provided');
      return;
    }

    localStorage.setItem("selectedLocationId", locationId.toString());
    localStorage.setItem("selectedLocationName", locationName.toString());
    setSelectedLocation(locationName);

    const route = getRoleRoute(role);
    if (route) {
      navigate(route);
    } else {
      console.error('Invalid role or role path not found:', role);
    }
  };

  if (loading) {
    return (
      <div className="global-dashboard-page">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="global-dashboard-page">
      <div
        className="background-image"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />

      <Navbar
        bg="white"
        expand="md"
        fixed="top"
        className="navbar-custom shadow-sm border-bottom"
      >
        <Container fluid className="px-0">
          <Navbar.Brand className="navbar-brand-custom">
            <Image src={logo} height={60} width={163} alt="Logo" />
            <div className="vertical-line" />
            <span className="fw-semibold text-dark">Global Dashboard</span>
          </Navbar.Brand>

          <Navbar.Toggle aria-controls="header-navbar" />

          <Navbar.Collapse id="header-navbar" className="justify-content-end">
            <Nav className="nav-section">
              <span className="d-none d-sm-block fw-medium text-dark user-name">
                {userName || "User"}
              </span>
              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="link"
                  className="d-flex align-items-center p-0 border-0 bg-transparent"
                  id="user-dropdown"
                  style={{ marginTop: '-5px' }}
                  bsPrefix="custom-dropdown-toggle"
                >
                  <Image
                    src={user}
                    height={28}
                    width={28}
                    className="me-2"
                    roundedCircle
                    alt="User Avatar"
                  />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={handleSignOut}>
                    Sign Out
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <div className="main-content">
        <div className="welcome-section">
          <h2 className="welcome-title-text">Welcome To</h2>
          <h1 className="portal-title">Feed Assay Portal</h1>

          <Dropdown className="mx-auto">
            <Dropdown.Toggle
              className="location-select"
              variant="light"
              disabled={locationsLoading || locations.length === 0}
            >
              {locationsLoading ? "Loading locations..." : selectedLocation}
            </Dropdown.Toggle>
            <Dropdown.Menu className="w-100" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {locations.length > 0 ? (
                locations.map((location) => (
                  <Dropdown.Item
                    key={location.locationId}
                    onClick={() => handleLocationChange(location.locationId, location.name)}
                  >
                    {location.name}
                  </Dropdown.Item>
                ))
              ) : (
                <Dropdown.Item disabled>
                  {locationsLoading ? "Loading..." : "No locations available"}
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

export default GlobalDashboard;
